import { StoryblokComponent } from "gatsby-source-storyblok"

import GalleryModal from "../../../../experiments/GalleryModal"
import RecommendedProductCard from "../../../../experiments/RecommendedProductCard"

import Accordion from "@components/elements/V2/Accordion"
import TextButton from "@components/elements/V2/Buttons/TextButton"
import Typography from "@components/elements/V2/Typography"
import ProductDetails from "@components/experiments/InfoSection/ProductDetails"
import LayoutItem from "@components/V2/Layouts/LayoutItem"
import type { GalleryProps } from "@components/V2/Sections/Info/Gallery"
import Gallery from "@components/V2/Sections/Info/Gallery"
import { Color } from "@constants/V2/color"
import { Industry } from "@constants/V2/industry"
import { useGetPurchasableItem } from "@hooks/V2/storyblok"
import { useGetStoryblokItem } from "@hooks/V2/storyblok/useItem"
import { itemAlreadyInCart } from "@utils/shop"
import React, { useState } from "react"

type Props = {
  name: string
  description?: string
  descriptionHeader?: string
  productUrl: string | YocoCom.YocoLink | undefined
  price: string
  originalPrice?: string
  priceSubscriptText?: string
  footerContent?: React.ReactNode
  contentSections: Storyblok.InfoSection[]
  recommendedProducts?: (string | Storyblok.Product | Storyblok.Bundle)[]
  overrideShowDiscount?: boolean
  mobileGridOrder?: number
  badge?: YocoCom.Badge
  tags?: Storyblok.ProductTag[]
  onPromotion: boolean
  skus?: string[]
  paymentStrip?: string
  showRatings: boolean
  boxContent?: {
    title: string
    imageUrl?: string
    imageAlt?: string
  }[]
  disclaimer?: {
    richText?: Storyblok.RichTextBlock
    color: Color
  }
  suitedFor?: string
  industries?: Industry[]
  worksWith?: string[]
  variantSelector?: React.ReactNode
  variantSkus?: string[][]
  variantDescription?: string
} & GalleryProps

const InfoSection = ({
  name,
  description,
  descriptionHeader,
  productUrl,
  galleryItems,
  price,
  originalPrice,
  priceSubscriptText,
  onPromotion,
  footerContent,
  contentSections,
  recommendedProducts,
  overrideShowDiscount,
  mobileGridOrder,
  badge,
  tags,
  skus,
  paymentStrip,
  showRatings,
  boxContent,
  disclaimer,
  suitedFor,
  industries,
  worksWith,
  variantSelector,
  variantSkus,
  variantDescription,
}: Props) => {
  const getStoryblokItem = useGetStoryblokItem()
  const getPurchasableItem = useGetPurchasableItem()

  const [isOpen, setIsOpen] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [initialSlide, setInitialSlide] = useState(0)

  const handleOpen = (index: number) => {
    setInitialSlide(index)
    setIsVisible(true)
    setIsOpen(true)
  }

  const handleClose = (value: boolean) => {
    setIsOpen(value)
    setTimeout(() => setIsVisible(false), 300)
  }

  const resolvableItems = (recommendedProducts ?? [])
    .map((item) => getStoryblokItem(item))
    .filter((item) => item.content !== null)

  const areAllRecommendedProductsInCart =
    resolvableItems.length > 0 &&
    resolvableItems.every((item) =>
      itemAlreadyInCart(getPurchasableItem(item.id, item.content!))
    )

  return (
    <>
      {isVisible && (
        <GalleryModal
          galleryItems={galleryItems.map((item) => ({
            imageFilename: item.image.filename,
            videoFilename: item.video?.filename,
            imageAlt: item.image.alt,
          }))}
          open={isOpen}
          initialSlide={initialSlide}
          handleClose={handleClose}
          productName={name}
        />
      )}

      <LayoutItem
        mobileGridOrder={mobileGridOrder}
        as="div"
        className="relative col-span-4 mt-20 self-start max-md:-mx-gutter-sm max-sm:!-mx-gutter-xs md:sticky md:top-[92px] md:col-span-3 md:mt-0 md:min-h-[600px] lg:col-span-6"
      >
        <div className="pb-12 md:pb-16">
          <TextButton
            style="text"
            text="YOCO SHOP"
            textSize="subscript-sm"
            linkUrl={`${process.env.GATSBY_PREFIX_PATH || ""}/shop`}
            color={Color.Black}
            className="pl-gutter-xs pr-10 opacity-70 md:pl-0"
          />
          <TextButton
            style="text"
            text={name}
            textSize="subscript-sm"
            linkUrl={productUrl}
            color={Color.Black}
          />
        </div>
        <Gallery
          galleryItems={galleryItems}
          badge={badge}
          onOpen={handleOpen}
          hasLoop
        />
      </LayoutItem>

      <LayoutItem
        mobileGridOrder={mobileGridOrder}
        as="div"
        className="relative col-span-4 md:col-span-3 md:pt-64 lg:col-span-6 lg:pt-32"
      >
        <ProductDetails
          name={name}
          price={price}
          originalPrice={originalPrice}
          priceSubscriptText={priceSubscriptText}
          onPromotion={onPromotion}
          overrideShowDiscount={overrideShowDiscount}
          showRatings={showRatings}
          skus={skus}
          tags={tags}
          disclaimer={disclaimer}
          description={description}
          descriptionHeader={descriptionHeader}
          paymentStrip={paymentStrip}
          boxContent={boxContent}
          industries={industries}
          suitedFor={suitedFor}
          worksWith={worksWith}
          variantSelector={variantSelector}
          variantSkus={variantSkus}
          variantDescription={variantDescription}
        />

        {recommendedProducts &&
          recommendedProducts.length > 0 &&
          !areAllRecommendedProductsInCart && (
            <div className="my-24">
              <Typography
                font="grotesk"
                size="body-lg"
                weight="medium"
                text="Frequently bought together"
                color={Color.Black}
                className="mb-[15px]"
              />

              {recommendedProducts?.map(
                (item: Storyblok.Product | Storyblok.Bundle | string) => (
                  <RecommendedProductCard
                    key={typeof item === "string" ? item : item.uuid}
                    product={item}
                  />
                )
              )}
            </div>
          )}

        {contentSections?.map((section, index) => {
          return (
            <div key={`${section.title}-${index}`}>
              {index > 0 && <hr className="my-16 border-gray" />}
              <Accordion
                key={`${index}`}
                title={section.title}
                textColor={Color.Black}
                iconColor={Color.Black}
                hideIcon
                contentLeftPadding={section.contentLeftPadding}
                openByDefault
                disabled
              >
                <div>
                  {section.subTitle && (
                    <Typography
                      text={section.subTitle}
                      font="grotesk"
                      color={Color.Charcoal}
                      size="body-md"
                      weight="medium"
                      className="pb-16"
                    />
                  )}
                  {section.description && (
                    <Typography
                      text={section.description}
                      font="grotesk"
                      color={Color.Charcoal}
                      size="body-md"
                      weight="book"
                      className="pb-16"
                    />
                  )}
                  {section.content?.map((content) => (
                    <div key={content._uid} className="py-4">
                      <StoryblokComponent
                        blok={content}
                        className="!flex-row"
                      />
                    </div>
                  ))}
                </div>
              </Accordion>
            </div>
          )
        })}

        {footerContent}
      </LayoutItem>
    </>
  )
}

export default InfoSection
